import { Link } from 'react-router-dom';
import { 
  Building2, Check, ArrowRight, FileCheck, ChevronRight,
  Clock, AlertTriangle, Zap
} from 'lucide-react';
import SEO from '../components/shared/SEO';

// Phase Card Component
const PhaseCard = ({ number, title, description, isLast }) => (
  <div className="flex items-start gap-4 relative">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 bg-[#CD7F32]/20 border border-[#CD7F32]/50 flex items-center justify-center">
        <span className="text-[#CD7F32] font-bold">{number}</span>
      </div>
      {!isLast && (
        <div className="w-px h-16 bg-gradient-to-b from-[#CD7F32]/50 to-transparent" />
      )}
    </div>
    <div className="pb-8">
      <h4 className="text-white font-bold text-lg">{title}</h4>
      <p className="text-white/50 text-sm mt-1">{description}</p>
    </div>
  </div>
);

// Package Card Component
const PackageCard = ({ tier, name, subtitle, price, color, features, popular, icon: Icon }) => {
  return (
    <div 
      className={`relative bg-[#121212] border transition-all duration-300 flex flex-col ${
        popular 
          ? 'border-[#D4AF37] shadow-lg shadow-[#D4AF37]/10 md:scale-105 z-10' 
          : 'border-white/10 hover:border-white/20'
      }`}
      data-testid={`build-package-${tier}`}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-[#D4AF37] text-black px-4 py-1 text-xs font-bold uppercase tracking-wider">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="w-10 h-10 flex items-center justify-center"
            style={{ backgroundColor: `${color}20`, border: `1px solid ${color}` }}
          >
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>
              {name}
            </span>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-white uppercase">
              {subtitle}
            </h3>
          </div>
        </div>
        
        <div className="mt-4">
          <span className="text-white/50 text-sm">One-Time Investment</span>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-white">${price.toLocaleString()}</span>
          </div>
          <p className="text-white/40 text-xs mt-1">No recurring fees</p>
        </div>
      </div>
      
      <div className="p-6 flex-1">
        <ul className="space-y-3">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color }} />
              <span className="text-white/70 text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="p-6 pt-0">
        <Link
          to={`/build/apply?tier=${tier}`}
          className={`w-full py-4 text-sm font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
            popular 
              ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]' 
              : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
          }`}
          data-testid={`apply-${tier}-btn`}
        >
          Apply Now
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

const BuildPage = () => {
  const phases = [
    { number: 1, title: 'Intake', description: 'Document collection and initial business setup' },
    { number: 2, title: 'Formation', description: 'LLC formation and EIN registration guidance' },
    { number: 3, title: 'Authority', description: 'DOT/MC authority filing and processing' },
    { number: 4, title: 'Compliance Handoff', description: 'Compliance setup and monitoring activation' },
    { number: 5, title: 'Launch Activation', description: 'Final checks and go-live preparation' },
  ];

  const packages = [
    {
      tier: 'foundation',
      name: 'Foundation',
      subtitle: 'Essential Start',
      price: 997,
      color: '#CD7F32',
      features: [
        'LLC formation guidance',
        'EIN registration assistance',
        'DOT number filing guidance',
        'MC authority filing guidance',
        'BOC-3 coordination',
        'UCR guidance',
        'IRP & IFTA checklist',
        '30 Days Compliance Support',
        '30 Days Dispatch Support',
        'Email support',
      ],
      icon: Building2,
    },
    {
      tier: 'accelerator',
      name: 'Accelerator',
      subtitle: 'Authority + Compliance',
      price: 1997,
      color: '#C0C0C0',
      features: [
        'Everything in Foundation',
        'Drug & Alcohol Consortium setup',
        'Clearinghouse registration guidance',
        'Compliance calendar setup',
        '60 Days Compliance Monitoring',
        'Carrier packet template',
        '60 Days Dispatch Support',
        'Priority email support',
      ],
      icon: FileCheck,
    },
    {
      tier: 'elite',
      name: 'Elite',
      subtitle: 'Full Business Launch',
      price: 2997,
      color: '#D4AF37',
      popular: true,
      features: [
        'Everything in Accelerator',
        'Business bank account setup guidance',
        'Factoring company assistance',
        'Fuel card setup guidance',
        'ELD consultation',
        '90 Days Compliance Support',
        'Business growth blueprint',
        '90 Days Dispatch Support',
        'Dedicated account manager',
      ],
      icon: Zap,
    },
  ];

  return (
    <div data-testid="build-page" className="bg-[#0A0A0A]">
      <SEO 
        title="BUILD - Obtain Your Authority | Queen B Logistics"
        description="Structured launch infrastructure for serious carriers. Phase-tracked system from business formation through authority activation. $997 - $2,997 one-time."
        canonical="https://queenblogistics.com/build"
        keywords="MC authority, DOT number, trucking business startup, owner operator startup, freight carrier launch, LLC formation trucking"
      />

      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-black to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className="text-white/50 hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4 text-white/30" />
              <span className="text-[#CD7F32]">Build</span>
            </div>

            {/* Title Block */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#CD7F32]/10 flex items-center justify-center border border-[#CD7F32]/30">
                <Building2 className="w-8 h-8 text-[#CD7F32]" />
              </div>
              <div>
                <span className="text-[#CD7F32] text-sm font-bold uppercase tracking-widest">
                  Build Your Business
                </span>
                <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
                  Obtain Your Authority
                </h1>
              </div>
            </div>

            <p className="text-xl md:text-2xl text-white/80 font-medium mb-6">
              Structured launch infrastructure for serious carriers.
            </p>
            
            <p className="text-white/50 text-lg leading-relaxed max-w-2xl mb-10">
              The Signature Program is a structured, phase-based system guiding you from 
              business formation through authority activation and launch readiness.
            </p>

            <Link
              to="/build/apply"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#CD7F32] text-black font-bold uppercase tracking-wider hover:bg-[#CD7F32]/90 transition-colors"
              data-testid="build-cta"
            >
              Apply for Signature Program
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* 5-Phase System */}
      <section className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-[#CD7F32] text-sm font-bold uppercase tracking-widest">
                Structured Process
              </span>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-6">
                5-Phase Launch System
              </h2>
              <p className="text-white/50 text-lg leading-relaxed mb-8">
                Every successful authority launch follows the same structured path. 
                Our phase-tracked system ensures nothing falls through the cracks.
              </p>
              
              <div className="bg-[#CD7F32]/10 border border-[#CD7F32]/30 p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#CD7F32]" />
                  <span className="text-white font-medium">Average completion: 8-12 weeks</span>
                </div>
                <p className="text-white/50 text-sm mt-2">
                  Timeline varies based on government processing times and document readiness.
                </p>
              </div>
            </div>
            
            <div className="bg-[#121212] border border-white/10 p-8">
              {phases.map((phase, i) => (
                <PhaseCard 
                  key={phase.number} 
                  {...phase} 
                  isLast={i === phases.length - 1}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#CD7F32] text-sm font-bold uppercase tracking-widest">
              Pricing
            </span>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-4">
              Choose Your Launch Package
            </h2>
            <div className="w-24 h-1 bg-[#CD7F32] mx-auto mb-6" />
            <p className="text-white/50 max-w-2xl mx-auto">
              One-time investment. No recurring fees. Just structured guidance to launch your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {packages.map((pkg) => (
              <PackageCard key={pkg.tier} {...pkg} />
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-16 bg-amber-500/5 border-y border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-16 h-16 bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h3 className="text-amber-400 font-bold text-xl mb-2">
                Important Disclaimer
              </h3>
              <p className="text-white/70 leading-relaxed">
                Queen B Logistics provides <strong className="text-white">structured guidance and monitoring services only</strong>. 
                Clients are responsible for completing their own government filings. We do not file documents on your behalf. 
                All government filing fees, insurance costs, and other business expenses are the client's responsibility. 
                Timeline estimates are subject to government processing times.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-8">
            What Happens After You Build?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link 
              to="/protect"
              className="bg-[#121212] border border-white/10 p-6 hover:border-[#C0C0C0]/50 transition-colors group"
            >
              <div className="w-12 h-12 bg-[#C0C0C0]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-[#C0C0C0] font-bold">→</span>
              </div>
              <h4 className="text-white font-bold text-lg mb-2">Protect Your Authority</h4>
              <p className="text-white/50 text-sm">
                Once your MC is active, compliance monitoring keeps you protected.
              </p>
              <span className="text-[#C0C0C0] text-sm mt-4 inline-block group-hover:underline">
                Learn about Compliance →
              </span>
            </Link>
            
            <Link 
              to="/grow"
              className="bg-[#121212] border border-white/10 p-6 hover:border-[#D4AF37]/50 transition-colors group"
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-[#D4AF37] font-bold">→</span>
              </div>
              <h4 className="text-white font-bold text-lg mb-2">Grow Your Revenue</h4>
              <p className="text-white/50 text-sm">
                Strategic dispatch services to build consistent, profitable operations.
              </p>
              <span className="text-[#D4AF37] text-sm mt-4 inline-block group-hover:underline">
                Learn about Dispatch →
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Ready to <span className="text-[#CD7F32]">Build?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            Start your application today. Our pre-qualification process ensures you're ready to launch.
          </p>
          <Link
            to="/build/apply"
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
            data-testid="build-final-cta"
          >
            Apply for Signature Program
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer Disclaimer */}
      <section className="py-8 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-white/30 text-xs text-center">
            Queen B Logistics provides administrative business formation and compliance support services. 
            We are not a law firm and do not provide legal advice. Approval timelines vary by state and FMCSA processing times.
          </p>
        </div>
      </section>
    </div>
  );
};

export default BuildPage;
